import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { Button, Col, Modal, Row } from 'react-bootstrap-5';
import { fetchLastFetchedAt, fetchOwners } from '../../../helpers/prospect_api';
import pluralize from 'pluralize';
import type { OwnershipsResponse } from 'bundles/connect/types';

type Props = {
  activityConsole?: boolean;
  insightsPropertyId: string;
  remainingCredits: number;
  setRemainingCredits?: React.Dispatch<React.SetStateAction<number>>;
  trialUser?: boolean;
  visible?: boolean;
  onUnlock?: (results: OwnershipsResponse) => void;
}


function RefreshOwnersModal({
  activityConsole,
  insightsPropertyId,
  remainingCredits,
  setRemainingCredits,
  trialUser,
  visible,
  onUnlock
}: Props) {
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const [show, setShow] = useState(false);

  const onRefresh = () => {
    setOwners(true);
    setShow(false);
  };

  const setOwners = useCallback((useCredit) => {
    fetchOwners(insightsPropertyId, useCredit, true, true)
      .then((results) => {
        if (onUnlock) {
          onUnlock(results);
        } else {
          window.location.reload();
        }
        if (useCredit) {
          setRemainingCredits && setRemainingCredits(remainingCredits - 1);
          setLastFetchedAt(new Date());
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 422) {
          if (error.response.data['remaining_credits'] !== undefined) {
            setRemainingCredits && setRemainingCredits(error.response.data['remaining_credits']);
          }
        }
      });
  }, [insightsPropertyId]);

  useEffect(() => {
    if (visible) {
      fetchLastFetchedAt(insightsPropertyId).then((res) => {
        if (res.lastFetched) {
          setLastFetchedAt(new Date(res.lastFetched));
          setRemainingCredits && setRemainingCredits(res.remainingCredits);
        }
      });
    }
  }, [visible]);

  return (
    <>
      <Modal centered show={show}>
        <Modal.Header className="flex-row" closeButton onHide={() => setShow(false)}>
          <Modal.Title>Are you sure?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>A credit will still be consumed even if no new contacts are found.</p>
          <p>You have {remainingCredits} {pluralize('credit', remainingCredits)} remaining
            {trialUser ? ' during your trial' : ''}.&nbsp;
            Unlocking this owner information will use 1 credit.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="tertiary" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button disabled={remainingCredits === 0} onClick={() => onRefresh()}>
            Refresh
          </Button>
        </Modal.Footer>
      </Modal>
      {activityConsole ? (
        <Button
          variant="ghost"
          onClick={() => setShow(true)}
        >
          Refresh Contact Data
        </Button>
      ) :
        (
          <Row className="mb-4 align-items-center">
            <Col>
              {lastFetchedAt && (
                <div className="text-muted small">Fetched on {lastFetchedAt.toLocaleDateString()}</div>
              )}
            </Col>
            {lastFetchedAt && (
              <Col xs="auto">
                <Button size="sm" variant="link" onClick={() => setShow(true)}>
                  Refresh Contacts
                </Button>
              </Col>
            )}
          </Row>
        )}
    </>
  );

}

const mapStateToProps = ({ ui: { profile: { trialUser } } }) => ({ trialUser });
export default connect(mapStateToProps)(RefreshOwnersModal);
